import { Field, FieldProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PaloField } from '@synergy/components-palo';
import { PoolNumberTextfield } from '@synergy/components-next';
import { ReactElement } from 'react';

import { InvoiceSteps } from '../../../models/invoice-steps';
import { PaloPositionEditModal } from '../../../../../../common/components/palo/palo-position-edit-modal';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { invoiceDetailStepperStateSelector } from '../../../store/invoice-detail-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

interface PositionEditModalProps {
    showPositionEditModal: boolean;
    onModalClose: () => void;
    positionIndex?: number;
    currency: string;
    taxRates: TaxRate[];
    onTaxRateSelected: (index: number, taxRate: TaxRate | null) => void;
    onDuplicate?: () => void;
    onInsertAbove?: () => void;
    onInsertBelow?: () => void;
    onDelete?: () => void;
}

/** One invoice position, in the dialog every document shares (#2208). */
export const PositionEditModal = (props: PositionEditModalProps): ReactElement => {
    const {
        showPositionEditModal,
        onModalClose,
        positionIndex,
        currency,
        taxRates,
        onTaxRateSelected,
        onDuplicate,
        onInsertAbove,
        onInsertBelow,
        onDelete,
    } = props;
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Positions);

    return (
        <PaloPositionEditModal
            open={showPositionEditModal}
            index={positionIndex}
            readonly={readonly}
            currency={currency}
            /* The invoice books a position against a counter account, and the
               tax follows from it — the same field the row carries. */
            extraFields={prefix => (
                <>
                    <PaloField label={<FormattedMessage id="app.common.counterAccount" />} name={`${prefix}.taxRate`}>
                        <TaxRateAutocomplete
                            name={`${prefix}.taxRate`}
                            disabled={readonly}
                            taxRates={taxRates}
                            onValueChange={taxRate =>
                                positionIndex !== undefined && onTaxRateSelected(positionIndex, taxRate)
                            }
                            showLabel={false}
                        />
                    </PaloField>
                    <PaloField label={<FormattedMessage id="app.table.column.tax" />} name={`${prefix}.tax`}>
                        <Field name={`${prefix}.tax`}>
                            {({ field }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    {...field}
                                    type="dense"
                                    readonly={readonly}
                                    suffix=" %"
                                    textAlign="right"
                                    fullWidth={true}
                                />
                            )}
                        </Field>
                    </PaloField>
                </>
            )}
            onDuplicate={onDuplicate}
            onInsertAbove={onInsertAbove}
            onInsertBelow={onInsertBelow}
            onDelete={onDelete}
            onClose={onModalClose}
        />
    );
};
